<template>
    <div class="datagrid-wrapper">
        <div id="users-overview">
            <div class="row">
                <div class="col-md-12 title-holder">
                    <h1>{{ table_title }}</h1>
                    <div v-if="can_create_user" class="grid-buttons" data-type="buttons">
                        <button id="start-create-user" class="btn btn-light" href="#">Create User</button>
                    </div>
                    <div class="search-form-mask open">
                        <form role="search" method="get" class="form-inline search-form">
                            <div class="form-group">
                                <input v-model.lazy="search" class="sb-search-input form-control"
                                       placeholder="Search user"/>
                            </div>
                            <div v-if="search">
                                <button @click.prevent="reset" type="reset" class="btn-icon btn-light sb-icon-reset">
                                <span class="fa fa-close">
                                    <span class="visually-hidden">Reset</span>
                                </span>
                                </button>
                            </div>
                            <button @click.prevent="getUsers" type="submit" class="btn btn-light sb-icon-search">
                            <span class="fa fa-search">
                                <span class="visually-hidden">Search</span>
                            </span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <table class="datagrid table">
                    <thead>
                    <tr>
                        <th v-for="header in table_headers">
                            <span class="btn-link">{{ header }}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="!users || !users.length || isLoading">
                        <td :colspan="table_headers.length">
                            <div v-if="isLoading">
                                <span class="spinner visible anim-spin"></span>
                            </div>
                            <div v-else>
                                No users found
                            </div>
                        </td>
                    </tr>
                    <tr v-else v-for="user in users" v-bind:key="user.id" ref="userRows">
                        <td v-for="column in table_columns">
                            <div v-if="column === 'name'">
                                <a :href="`/user/${user.id}`" data-bs-toggle="tooltip" data-bs-html="true"
                                   :title="tooltipHtml(user.name, user.email, user.phone)">
                                    {{ user.name }}
                                </a>
                            </div>
                            <div v-else-if="column === 'status'" v-html="statusHtml(user.status)"></div>
                            <div v-else-if="column === 'roles'" v-html="rolesHtml(user.roles)"></div>
                            <div v-else-if="column === 'company_names'" v-html="companyHtml(user.company_names)"></div>
                            <div v-else-if="column === 'tag_numbers'" v-html="tagNumberHtml(user.tag_numbers)"></div>
                            <div v-else-if="column === 'tag_ids'" v-html="tagStatusHtml(user.tag_ids)"></div>
                            <div v-else-if="column === 'action'">
                                <div v-if="user.show_spinner">
                                    <span class="spinner anim-spin"></span>
                                </div>
                                <div v-else-if="user.can_block_user || user.can_remove_user">
                                    <a v-if="user.can_block_user" @click="blockUser(user)"  :href="`/user/${user.id}/blockuser`"
                                        :data-type-action="user.block_value" :class="user.block_value + '-user user-overview-block btn btn-link link-button'">
                                        {{ user.block_text }}
                                    </a>
                                    <span v-if="user.can_block_user && user.can_remove_user"> - </span>
                                    <a v-if="user.can_remove_user" :href="`/user/${user.id}/remove`"
                                       class="js-access-remove-user btn btn-link link-button" :data-user-id="user.id">Delete</a>
                                </div>
                            </div>
                            <div v-else v-html="user[column]"></div>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colspan="3">
                            <simple-pagination
                                v-model="paginatedResponse"
                                v-on:page-change-requested="changePageData"
                                prev-text="< Previous"
                                next-text="Next >"
                                v-on:loading-started="loadingStarted"
                            >
                            </simple-pagination>
                        </td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>
<script setup>
import {nextTick, onMounted, ref, watch} from 'vue';
import {initTooltips} from "../../Services/Tooltip";
import clp from "@/_clp.js";
import axios from 'axios';

const props = defineProps({
    api_endpoint: String,
    table_title: String,
    can_create_user: Boolean,
    table_headers: Array,
    table_columns: Array,
});

const users = ref([]);
const paginate = ref(10);
const search = ref('');
const isLoading = ref(false);
const currentPage = ref(1);
const paginatedResponse = ref({});

watch(paginate, () => {
    getUsers();
});

function tooltipHtml(name, email, phone) {
    let tooltip = '';

    if (name) {
        tooltip += name + '<br/>';
    }
    if (email) {
        tooltip += email + '<br/>';
    }
    if (phone) {
        tooltip += phone + '<br/>';
    }

    return tooltip;
}
function statusHtml(status) {
    let cssClass;

    if (status === 'blocked') {
        cssClass = 'text-danger';
    }
    else if (status === 'inactive') {
        cssClass = 'text-inactive';
    } else {
        cssClass = 'text-active';
    }

    return '<span class="' + cssClass + '">' + status + '</span>';
}
function rolesHtml(roles) {
    if (roles.length === 0) {
        return '';
    }

    return roles.join(', ');
}
function companyHtml(companyNames) {
    if (typeof companyNames === 'undefined' || companyNames.length === 0) {
        return '';
    }

    return companyNames.join('<br/>');
}
function tagNumberHtml(tags) {
    if (tags.length === 0) {
        return '<span class="text-inactive">none</span>';
    }

    return tags.join('<br/>');
}
function tagStatusHtml(tagIds) {
    if (tagIds.length === 0) {
        return '';
    }

    return tagIds.map(function(value) {
        return '<span class="spinner visible anim-spin"></span>'
            + '<span data-clp="status" data-type="tag" data-value="' + value + '"></span>';
    }).join('<br/>');
}
function initTooltipsFn() {
    nextTick(() => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        initTooltips(tooltipTriggerList);
    });
}
async function getUsers(page = 1) {
    isLoading.value = true;
    const response = await axios.get(props.api_endpoint, {
        params: {
            page,
            paginate: paginate.value,
            q: search.value,
        }
    });
    users.value = response.data.data;
    paginatedResponse.value = response.data;
    currentPage.value = response.data.current_page;
    afterDataUpdate();
}
function reset() {
    search.value = '';
    getUsers();
}
function refreshHardware() {
    // needed for /helper/get_clp_status_for_tags call
    setTimeout(() => {
        clp.refreshHardware($(document));
    }, 2000);
}
function blockUser(user) {
    user.show_spinner = true;
    setTimeout(() => {
        user.show_spinner = false;
        user.block_text = user.is_blocked ? 'Block user' : 'Unblock user';
        getUsers(currentPage.value);
    }, 2000);
}
async function changePageData(data) {
    users.value = [];

    await nextTick();
    users.value = data;
    afterDataUpdate();
}
function afterDataUpdate() {
    refreshHardware();
    initTooltipsFn();
    isLoading.value = false;
}
function loadingStarted() {
    isLoading.value = true;
}
onMounted(() => {
    getUsers();
});
</script>
<style scoped>
.btn-link,.btn-link:hover {
    letter-spacing: normal;
    padding: 0 30px 0 0;
    margin: 0;
    vertical-align: bottom;
    color: #babfbf;
    font-weight: bold;
    font-size: 11px;
    outline: none !important;
    border: 0;
    text-decoration: none;
    white-space: nowrap;
}
</style>
